import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, TextField, Button, Table, TableHead, TableRow,
  TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Drawer, List, ListItem, ListItemText,
  Paper, TableContainer, Tooltip, Slide, InputAdornment, Skeleton, CircularProgress, Divider, Link
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Add, Edit, Delete, Inventory, Search as SearchIcon,
  Save, Close
} from '@mui/icons-material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import api from '../../../../services/api';
import { buildWhatsappLink } from '../../../../utils/whatsapp';

const TransitionUp = React.forwardRef(function TransitionUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/** Helpers de telefone BR */
const onlyDigits = (v = '') => (v || '').replace(/\D/g, '');
const maskPhoneBR = (digits) => {
  // até 11 dígitos: (DD) 9XXXX-XXXX ou (DD) XXXX-XXXX
  const d = onlyDigits(digits).slice(0, 11);
  if (d.length <= 10) {
    // fixo 8 dígitos
    const part1 = d.slice(0, 2);
    const part2 = d.slice(2, 6);
    const part3 = d.slice(6, 10);
    if (d.length <= 2) return part1 ? `(${part1}` : '';
    if (d.length <= 6) return `(${part1}) ${part2}`;
    return `(${part1}) ${part2}-${part3}`;
  }
  // celular 9 dígitos
  const dd = d.slice(0, 2);
  const p2 = d.slice(2, 7);
  const p3 = d.slice(7, 11);
  return `(${dd}) ${p2}-${p3}`;
};
const Fornecedores = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [total, setTotal] = useState(0);

  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({ nome: '', nome_vendedor: '', whatsapp: '', endereco: '' });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerFornecedor, setDrawerFornecedor] = useState(null);
  const [drawerItems, setDrawerItems] = useState([]);
  const [insumoOptions, setInsumoOptions] = useState([]);
  const [insumosLoading, setInsumosLoading] = useState(false);
  const [insumosQuery, setInsumosQuery] = useState('');

  const totalPages = Math.ceil(total / perPage) || 1;

  // debounce busca
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchFornecedores = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/confereai/fornecedores', {
        params: { q: debouncedSearch || undefined, page, per_page: perPage },
      });
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setRows(list);
      setTotal(data.total ?? data.meta?.total ?? list.length);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar fornecedores');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFornecedores();
    // Omite `fetchFornecedores`; deps listam busca/página.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, page]);

  const handleOpenModal = (fornecedor = null) => {
    setCurrent(fornecedor);
    setForm({
      nome: fornecedor?.nome || '',
      nome_vendedor: fornecedor?.nome_vendedor || '',
      whatsapp: fornecedor?.whatsapp ? maskPhoneBR(fornecedor.whatsapp) : '', // aceita vindo sem máscara
      endereco: fornecedor?.endereco || '',
    });
    setOpenModal(true);
  };

  const handleSave = async () => {
    try {
      if (!form.nome.trim()) return toast.error('Informe o nome do fornecedor');

      // sanitiza whatsapp para só dígitos (opcional)
      const whatsappDigits = onlyDigits(form.whatsapp);
      if (whatsappDigits && whatsappDigits.length < 10) {
        return toast.error('WhatsApp inválido (DDD + número).');
      }

      setSaving(true);
      const payload = {
        nome: form.nome.trim(),
        nome_vendedor: form.nome_vendedor?.trim() || '',
        whatsapp: whatsappDigits || '', // envia dígitos para o back
        endereco: form.endereco?.trim() || '',
      };

      if (current?.id) {
        await api.put(`/confereai/fornecedores/${current.id}`, payload);
        toast.success('Fornecedor atualizado');
      } else {
        await api.post('/confereai/fornecedores', payload);
        toast.success('Fornecedor criado');
      }
      setOpenModal(false);
      fetchFornecedores();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar fornecedor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (fornecedor) => {
    if (!window.confirm('Excluir fornecedor?')) return;
    try {
      await api.delete(`/confereai/fornecedores/${fornecedor.id}`);
      toast.success('Fornecedor excluído');
      fetchFornecedores();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao excluir fornecedor');
    }
  };

  const openItensDrawer = async (fornecedor) => {
    setDrawerFornecedor(fornecedor);
    setDrawerOpen(true);
    await loadDrawerItens(fornecedor.id);
  };

  const loadDrawerItens = async (id) => {
    try {
      const { data } = await api.get(`/confereai/fornecedores/${id}/insumos`);
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setDrawerItems(list);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar insumos');
      setDrawerItems([]);
    }
  };

  // ---- busca de insumos com debounce
  const fetchInsumos = async (q) => {
    setInsumosLoading(true);
    try {
      const { data } = await api.get('/confereai/insumos', {
        params: { q: q || undefined, per_page: 12 },
      });
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setInsumoOptions(list);
    } catch (err) {
      console.error(err);
      setInsumoOptions([]);
    } finally {
      setInsumosLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => fetchInsumos(insumosQuery), 350);
    return () => clearTimeout(t);
  }, [insumosQuery]);

  const handleAddInsumo = async (insumo) => {
    if (!insumo || !drawerFornecedor) return;
    try {
      await api.post(`/confereai/fornecedores/${drawerFornecedor.id}/insumos`, {
        insumo_ids: [insumo.id],
      });
      toast.success('Insumo vinculado');
      await loadDrawerItens(drawerFornecedor.id);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao vincular insumo');
    }
  };

  const handleRemoveInsumo = async (insumo) => {
    if (!drawerFornecedor) return;
    try {
      await api.delete(`/confereai/fornecedores/${drawerFornecedor.id}/insumos/${insumo.id}`);
      toast.success('Insumo removido');
      await loadDrawerItens(drawerFornecedor.id);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao remover insumo');
    }
  };

  const existingInsumoIds = React.useMemo(() => {
    const ids = new Set();
    for (const it of drawerItems || []) {
      const id = it?.insumo_id ?? it?.id ?? it?.insumo?.id;
      if (id != null) ids.add(String(id));
    }
    return ids;
  }, [drawerItems]);

  const filteredInsumoOptions = React.useMemo(
    () => (insumoOptions || []).filter(opt => !existingInsumoIds.has(String(opt?.id))),
    [insumoOptions, existingInsumoIds]
  );

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          py: 1,
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 2
        }}
        component={motion.div}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #90caf9 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: 0.3
          }}
        >
          Fornecedores
        </Typography>

        <Box>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal()}>
            Novo Fornecedor
          </Button>
        </Box>
      </Box>

      {/* Filtro */}
      <Paper
        component={motion.div}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(250,250,250,0.7))',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              label="Buscar"
              fullWidth
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          background: '#fff'
        }}
      >
        <Table
          size="small"
          sx={{
            '& thead th': { bgcolor: 'grey.50', fontWeight: 600 },
            '& th, & td': { px: 2 },
            '& th:first-of-type, & td:first-of-type': { pl: 3 },
            '& th:last-of-type,  & td:last-of-type':  { pr: 3 },
            '& thead th:first-of-type': { borderTopLeftRadius: 12 },
            '& thead th:last-of-type':  { borderTopRightRadius: 12 },
            '& tbody tr:hover': { bgcolor: 'action.hover' },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Fornecedor</TableCell>
              <TableCell>Vendedor</TableCell>
              <TableCell>WhatsApp</TableCell>
              <TableCell>Endereço</TableCell>
              <TableCell width={160} align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <AnimatePresence initial={false}>
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell><Skeleton height={20} /></TableCell>
                    <TableCell><Skeleton height={20} width="40%" /></TableCell>
                    <TableCell><Skeleton height={20} width="60%" /></TableCell>
                    <TableCell><Skeleton height={20} width="50%" /></TableCell>
                    <TableCell><Skeleton height={20} /></TableCell>
                    <TableCell align="right"><Skeleton height={24} width={90} /></TableCell>
                  </TableRow>
                ))
              ) : rows.length > 0 ? (
                rows.map((row) => {
                  const waDigits = row.whatsapp ? onlyDigits(row.whatsapp) : '';
                  const waMask = waDigits ? maskPhoneBR(waDigits) : '—';
                  const waUrl = waDigits ? buildWhatsappLink(waDigits) : null;
                  return (
                    <TableRow
                      key={row.id}
                      component={motion.tr}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.15 }}
                      hover
                    >
                      <TableCell><Typography fontWeight={600}>{row.nome}</Typography></TableCell>
                      <TableCell>{row.nome_vendedor || '—'}</TableCell>
                      <TableCell>
                        {waUrl ? (
                          <Link href={waUrl} target="_blank" rel="noopener" underline="hover">
                            {waMask}
                          </Link>
                        ) : '—'}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{row.endereco || '—'}</TableCell>
                      <TableCell align="right">
                        {waUrl && (
                          <Tooltip title="Abrir WhatsApp">
                            <IconButton size="small" component="a" href={waUrl} target="_blank" rel="noopener" aria-label="Abrir WhatsApp">
                              <WhatsAppIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Ver/Vincular Insumos">
                          <IconButton size="small" onClick={() => openItensDrawer(row)} aria-label="Ver/Vincular Insumos">
                            <Inventory fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleOpenModal(row)} aria-label="Editar fornecedor">
                            <Edit fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" onClick={() => handleDelete(row)} aria-label="Excluir fornecedor">
                            <Delete fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">Nenhum fornecedor encontrado</Typography>
                  </TableCell>
                </TableRow>
              )}
            </AnimatePresence>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      {totalPages > 1 && (
        <Box mt={2} display="flex" justifyContent="center" alignItems="center" gap={1}>
          <Button disabled={page === 1} onClick={() => setPage(page - 1)}>Anterior</Button>
          <Box px={2}>{page} / {totalPages}</Box>
          <Button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Próxima</Button>
        </Box>
      )}

      {/* Modal Fornecedor */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={TransitionUp}
        PaperProps={{
          component: motion.div,
          initial: { opacity: 0, y: 16, scale: 0.98 },
          animate: { opacity: 1, y: 0, scale: 1 },
          exit: { opacity: 0, y: 16, scale: 0.98 },
          transition: { duration: 0.2 },
          sx: { borderRadius: 3, overflow: 'hidden' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {current?.id ? 'Editar Fornecedor' : 'Novo Fornecedor'}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            margin="normal"
            label="Nome"
            fullWidth
            value={form.nome}
            onChange={(e) => setForm({ ...form, nome: e.target.value })}
          />
          <TextField
            margin="normal"
            label="Vendedor"
            fullWidth
            value={form.nome_vendedor}
            onChange={(e) => setForm({ ...form, nome_vendedor: e.target.value })}
          />
          <TextField
            margin="normal"
            label="WhatsApp"
            placeholder="(11) 9XXXX-XXXX"
            fullWidth
            value={form.whatsapp}
            onChange={(e) => {
              const masked = maskPhoneBR(e.target.value);
              setForm({ ...form, whatsapp: masked });
            }}
            inputProps={{ inputMode: 'numeric' }}
            helperText="Informe DDD + número. Enviaremos apenas os dígitos para a API."
          />
          <TextField
            margin="normal"
            label="Endereço"
            fullWidth
            value={form.endereco}
            onChange={(e) => setForm({ ...form, endereco: e.target.value })}
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button startIcon={<Close />} onClick={() => setOpenModal(false)}>Cancelar</Button>
          <Button
            startIcon={saving ? <CircularProgress size={18} /> : <Save />}
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drawer Insumos do Fornecedor */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        TransitionComponent={TransitionUp}
        ModalProps={{ keepMounted: true }}
        // garante que o Drawer fique acima do AppBar/cabeçalho
        sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
        PaperProps={{
          component: motion.div,
          initial: { x: 40, opacity: 0 },
          animate: { x: 0, opacity: 1 },
          exit: { x: 40, opacity: 0 },
          transition: { duration: 0.2 },
          sx: { width: { xs: '100%', sm: 380 } }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Insumos de {drawerFornecedor?.nome || '—'}
          </Typography>

          {/* SELECT de insumos (exclui os já adicionados) */}
          <Autocomplete
            options={filteredInsumoOptions}
            loading={insumosLoading}
            onOpen={() => fetchInsumos('')}
            getOptionLabel={(o) => o?.nome || ''}
            onInputChange={(e, v) => setInsumosQuery(v)}
            onChange={(e, v) => handleAddInsumo(v)}
            filterOptions={(x) => x}
            // Popper acima do cabeçalho
            slotProps={{
              popper: { sx: { zIndex: (theme) => theme.zIndex.modal + 11 } },
              paper: { elevation: 8 }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Adicionar insumo"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {insumosLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* LISTA numerada e estilizada */}
            <List disablePadding>
              {drawerItems.map((item, idx) => {
                const title =
                  item?.nome ??
                  item?.insumo_nome ??
                  item?.nome_insumo ??
                  item?.insumo?.nome ??
                  item?.name ??
                  '—';

                return (
                  <ListItem
                    key={item.id ?? `${idx}-${title}`}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveInsumo(item)}
                        aria-label="Remover insumo"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    }
                    component={motion.li}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.15 }}
                    sx={{
                      px: 2,
                      py: 1.1,
                      ...(idx !== drawerItems.length - 1 && {
                        borderBottom: '1px dashed',
                        borderColor: 'divider',
                      }),
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                      {/* bolinha numerada */}
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flex: '0 0 auto',
                        }}
                      >
                        {idx + 1}
                      </Box>

                      <Typography
                        fontWeight={600}
                        noWrap
                        title={title}
                        sx={{ flex: 1, minWidth: 0 }}
                      >
                        {title}
                      </Typography>
                    </Box>
                  </ListItem>
                );
              })}

              {drawerItems.length === 0 && !insumosLoading && (
                <ListItem>
                  <ListItemText primary="Nenhum insumo" />
                </ListItem>
              )}
            </List>
          


        </Box>
      </Drawer>

    </Box>
  );
};

export default Fornecedores;
